import classNames from 'classnames'
import LeftSide from './components/LeftSide/LeftSide'
import React, { ReactElement, useEffect } from 'react'
import RightSide from './components/RightSide/RightSide'
import { AWS_S3_URL } from 'utils/constants'
import { IFileUploader } from './interface'
import { useDropzone } from 'react-dropzone'
import { useFileUploader } from './components/hooks/useFileUploader'
import { useTranslation } from 'react-i18next'
import './FileUploader.scss'

const T_ROOT = 'fileUploader'

const FileUploader = ({
  defaultFileName,
  defaultFileURL,
  disabled,
  fileDesc,
  filesEntity,
  fileTitle,
  fileTypes,
  input,
  isFailUpload,
  loading,
  maxSize,
  meta,
  noClick = true,
  noDrag,
  onChange,
  onSubmitDocument,
  preloadedFile,
  title,
  tooltip
}: IFileUploader): ReactElement | null => {
  const { t } = useTranslation()
  const { errorMessage, fileSelected, handleClearSelection, isFileAccepted, isFileRejected, onDrop, setFileSelected } =
    useFileUploader(meta, input, onChange)

  const { getInputProps, getRootProps, isDragActive, isFileDialogActive, open } = useDropzone({
    accept: fileTypes,
    disabled,
    maxSize,
    minSize: 0,
    multiple: false,
    noClick,
    noDrag,
    noKeyboard: true,
    onDrop
  })

  const FILE_UPLOADER_CLASSES: string = classNames('file-uploader', {
    'file-uploader--disabled': disabled,
    'file-uploader--drag-active': isDragActive,
    'file-uploader--error': errorMessage || isFileRejected,
    'file-uploader--selected': isFileAccepted
  })
  useEffect(() => {
    if (fileSelected?.file && !fileSelected.isFilePreloaded) {
      onSubmitDocument && onSubmitDocument(fileSelected, fileTypes)
    }
  }, [fileSelected])

  useEffect(() => {
    if (filesEntity?.length) {
      const filesUploaded = filesEntity.filter((file: any) => file.idReferenceFile)
      if (filesUploaded?.length) {
        const file = preloadedFile && preloadedFile(input)
        setFileSelected(file || { file: undefined, rejected: false })
      }
    }
  }, [filesEntity])

  return (
    <div {...getRootProps({ className: FILE_UPLOADER_CLASSES })}>
      {isDragActive ? (
        <div className='drag-area'>
          <img src={`${AWS_S3_URL}/img/icons/drag-drop-file.svg`} alt='icon' className='image' />
          <p className='drag-area-text'>{t(`${T_ROOT}.dragHereYourFile`).toString()}</p>
        </div>
      ) : (
        <>
          <LeftSide
            defaultFileName={defaultFileName}
            defaultFileURL={defaultFileURL}
            disabled={disabled}
            errorMessage={errorMessage}
            fileTypes={fileTypes}
            isFileAccepted={isFileAccepted}
            isFileRejected={isFileRejected}
            tooltip={tooltip}
            fileName={fileSelected.file?.name ?? ''}
            fileTitle={fileTitle}
            fileDesc={fileDesc}
            isFailUpload={isFailUpload}
          />
          <RightSide
            defaultFileName={defaultFileName}
            defaultFileURL={defaultFileURL}
            disabled={disabled}
            getInputProps={getInputProps}
            input={input}
            isFileRejected={isFileRejected}
            isFileAccepted={isFileAccepted}
            title={title}
            open={open}
            isFileDialogActive={isFileDialogActive}
            isFailUpload={isFailUpload}
            onClickDelete={(e: React.MouseEvent<HTMLElement>): void => handleClearSelection(e)}
            noDrag={noDrag}
            loading={loading}
          />
        </>
      )}
    </div>
  )
}

export default FileUploader
